import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/store/auth";
import { toast } from "sonner";
import { Loader2, Sparkles, MessageSquare, GitBranch, CheckSquare, BarChart3, Repeat, Brain } from "lucide-react";

const FEATURES = [
  { icon: CheckSquare, label: "Task Lifecycle" },
  { icon: GitBranch, label: "Approval Chains" },
  { icon: Repeat, label: "Recurring Schedules" },
  { icon: MessageSquare, label: "WhatsApp First" },
  { icon: Brain, label: "AI Memory" },
  { icon: BarChart3, label: "ERP Reports" },
];

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("admin@team360.ai");
  const [password, setPassword] = useState("Admin@12345");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Invalid credentials");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left: Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-white border-r border-slate-200">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #334E7B 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full  blur-3xl opacity-40" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-sky-100 blur-3xl opacity-50" />

        <div className="relative flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <img src="https://skiverse.skicorp.in/upload/process/79871716008647color_logo_without_tagline.png" alt="logo" style={{width:'18%'}} />
            <div className="w-px h-9 bg-slate-300 flex-shrink-0" />
            <div className="flex flex-col text-[10px] font-bold uppercase tracking-widest text-slate-500 leading-[1.5]">
              <span>Task</span>
              <span>Management</span>
              <span>System</span>
            </div>
          </div>

          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#334E7B] border border-[#334E7B] text-white text-[11px] font-semibold uppercase tracking-wider mb-5">
              <Sparkles size={12} /> Powered by Claude AI
            </div>
            <h2 className="font-heading text-4xl xl:text-5xl font-bold tracking-tight leading-[1.1] text-slate-900">
              ERP-grade work, approvals &amp; memory
              <span className="block text-[#334E7B] mt-2">delivered through WhatsApp.</span>
            </h2>
            <p className="mt-5 text-slate-600 text-[15px] max-w-md leading-relaxed">
              Run tasks, approvals, recurring schedules, SLA tracking, performance reports
              and AI memory across your teams — control from your browser, execute on WhatsApp.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
              {FEATURES.map((f) => (
                <div key={f.label} className="bg-white/60 backdrop-blur border border-slate-200 rounded-lg p-3 hover:border-[#334E7B] hover:bg-white transition-all">
                  <f.icon size={16} className="text-[#334E7B] mb-1.5" strokeWidth={1.8} />
                  <div className="text-[11px] font-medium text-slate-700 leading-tight">{f.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[11px] text-slate-400">© SKIVERSE Task App · Built for serious teams.</div>
        </div>
      </div>

      {/* Right: Login form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-md erp-card-elevated p-10" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-7">
            <img
              src="https://skiverse.skicorp.in/upload/process/79871716008647color_logo_without_tagline.png"
              alt="Skiverse"
              className="h-9 w-auto object-contain"
            />
            <div className="w-px h-9 bg-slate-300 flex-shrink-0" />
            <div className="flex flex-col text-[9px] font-bold uppercase tracking-widest text-slate-500 leading-[1.4]">
              <span>Task</span>
              <span>Management</span>
              <span>System</span>
            </div>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Sign in</h1>
          <p className="text-sm text-slate-500 mt-2">Welcome back. Access your control center.</p>

          <div className="mt-7 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="erp-input mt-1.5"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="erp-input mt-1.5"
                data-testid="login-password"
              />
            </div>
            <button type="submit" disabled={submitting}
                    className="erp-btn-primary w-full !py-2.5"
                    data-testid="login-submit-btn">
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitting ? "Signing in..." : "Sign in"}
            </button>
          </div>

          {/* <div className="mt-7 p-3.5 bg-[#334E7B]/50 border border-[#334E7B] rounded-lg text-xs">
            <div className="font-semibold text-slate-700 mb-1">Demo credentials</div>
            <div className="text-slate-500 font-mono text-[11px]">admin@team360.ai · Admin@12345</div>
          </div> */}
        </form>
      </div>
    </div>
  );
}
